/**
 * Job Stats Component
 * Displays summary statistics cards for job progress
 * Shows total weight, diverted weight, and landfill weight
 */

import { formatWeight, formatPercentage } from '../../utils/formatting';
import type { JobDiversionStats } from '../../types/job';

interface JobStatsProps {
  stats: JobDiversionStats;
}

export function JobStats({ stats }: JobStatsProps) {
  const {
    total_weight,
    diverted_weight,
    landfill_weight,
    diversion_rate,
    target_diversion_rate,
  } = stats;

  const isCompliant = diversion_rate >= target_diversion_rate;

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {/* Total Weight Card */}
      <div className="card border-2 border-neutral-200">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm font-medium text-neutral-600 mb-1">
              Total Weight
            </p>
            <p className="text-3xl font-bold text-neutral-900">
              {formatWeight(total_weight)}
            </p>
          </div>
          <div className="p-3 bg-neutral-100 rounded-lg">
            <svg
              className="w-6 h-6 text-neutral-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
              />
            </svg>
          </div>
        </div>
        <p className="text-xs text-neutral-500 mt-2">
          All materials tracked on this job
        </p>
      </div>

      {/* Diverted Weight Card */}
      <div className="card border-2 border-accent-200 bg-accent-50/30">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm font-medium text-accent-700 mb-1">
              Diverted from Landfill
            </p>
            <p className="text-3xl font-bold text-accent-900">
              {formatWeight(diverted_weight)}
            </p>
          </div>
          <div className="p-3 bg-accent-100 rounded-lg">
            <svg
              className="w-6 h-6 text-accent-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
        </div>
        <p className="text-xs text-accent-700 mt-2">
          {formatPercentage(diversion_rate, 1)} diversion rate
        </p>
      </div>

      {/* Landfill Weight Card */}
      <div className="card border-2 border-red-200 bg-red-50/30">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm font-medium text-red-700 mb-1">
              Sent to Landfill
            </p>
            <p className="text-3xl font-bold text-red-900">
              {formatWeight(landfill_weight)}
            </p>
          </div>
          <div className="p-3 bg-red-100 rounded-lg">
            <svg
              className="w-6 h-6 text-red-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
              />
            </svg>
          </div>
        </div>
        <div className="mt-2">
          {isCompliant ? (
            <p className="text-xs text-neutral-600">
              Within acceptable limits
            </p>
          ) : (
            <p className="text-xs text-red-700">
              Reduce by{' '}
              {formatWeight(
                ((target_diversion_rate - diversion_rate) / 100) * total_weight
              )}{' '}
              to meet target
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
